'use client';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { ButtonFragmentFragment } from '@fm-nx/contentful-types';
import { StickyButton } from '../sticky-button/sticky-button';
import { Button } from '../button/button';
import Breadcrumb from '../breadcrumb/breadcrumb';
import classNames from 'classnames';

type PageIntroLevel1DisplayProps = {
    entryId?: string;
    title?: string | null;
    headerText?: string | null;
    headerParagraph?: string | null;
    button?: ButtonFragmentFragment | null;
    gradient?: 'blue-green-yellow' | 'yellow-salmon' | 'purple-blue' | 'teal-blue' | 'salmon-pink';
};

export const PageIntroLevel1Display = (props: PageIntroLevel1DisplayProps) => {
    const { headerText, headerParagraph, button, entryId, title, gradient = 'blue-green-yellow' } = props;
    const inspectorProps = useContentfulInspectorMode({ entryId });

    const gradientClass = classNames({
        'from-powder via-green-100 to-yellow-100': gradient === 'blue-green-yellow',
        'from-salmon to-yellow-100': gradient === 'yellow-salmon',
        'from-purple-300 to-blue-100': gradient === 'purple-blue',
        'from-teal-400 to-blue-400': gradient === 'teal-blue',
        'from-red-400 via-red-200 to-purple-200': gradient === 'salmon-pink',
    });

    return (
        <div
            className={classNames(
                'bg-radial-[194.95%_128.78%_at_95.09%_87.18%]',
                gradientClass,
                'rounded-b-(--radius-fluid-lg) pt-32 pb-2 md:pt-40 lg:pt-[161px] md:pb-20'
            )}
        >
            <div className="relative w-full mx-auto max-w-container">
                <div className={'mx-6 md:mx-0 grid grid-cols-1 gap-4 md:grid-cols-14'}>
                    <div className={'space-y-6 md:col-span-9 md:col-start-2 lg:col-start-2'}>
                        <Breadcrumb title={title} />
                        <h2
                            {...inspectorProps({
                                fieldId: 'title',
                            })}
                            className={'h-xxl'}
                        >
                            {headerText}
                        </h2>
                        {headerParagraph && (
                            <p
                                {...inspectorProps({
                                    fieldId: 'summary',
                                })}
                                className={'p-xl'}
                            >
                                {headerParagraph}
                            </p>
                        )}

                        <div className="md:hidden">
                            {/* Hidden at md because StickyButton replaces it */}
                            {button && (
                                <div className="mt-4">
                                    <Button button={button} />
                                </div>
                            )}
                        </div>

                        {button && (
                            <div className="hidden md:block absolute z-40 right-8 md:right-16 bottom-0">
                                <StickyButton button={button} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PageIntroLevel1Display;
